import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * HOLD A SORTED LIST'S ROW ORDER WHILE A ROW HAS UNSAVED CHANGES — owner
 * ruling 2026-10-11, of the crew Gantt: dragging a bar re-sorted the row at
 * once, often off the screen (*"it jumps to the correct sorting place but that
 * could be off the screen so its hard to see"*). The rule he chose:
 *
 *   - while `hold` is true the rows keep the order they had when it began —
 *     the bar changes, nothing else moves;
 *   - when `hold` ends (Save) the rows take their sorted order, and if the row
 *     that was being edited MOVED, `revealId` names it for ~2s so the caller
 *     can scroll to it and mark it;
 *   - a change of `sortKey` (the user clicking a sort control) re-sorts at
 *     once, hold or not — a deliberate "tidy up now".
 *
 * Presentation only: the rows' DATA is whatever `sorted` carries (the draft
 * on screen is still what Save stores); this decides their ORDER and nothing
 * else. A row that appears while held goes after the held rows, in sorted
 * order. A discard moves nothing back, so it reveals nothing.
 */

/** `sorted` re-ordered to follow `heldIds`; rows the list does not name go last, in `sorted` order. */
export function holdRowOrder(sorted, heldIds) {
  const rank = new Map(heldIds.map((id, i) => [id, i]));
  const known = sorted.filter((r) => rank.has(r.id)).sort((a, b) => rank.get(a.id) - rank.get(b.id));
  return [...known, ...sorted.filter((r) => !rank.has(r.id))];
}

/** How long `revealId` stays set, and how long after the hold ends a move still counts as its result. */
export const HELD_ROW_REVEAL_MS = 2000;
const SETTLE_WINDOW_MS = 5000;

/**
 * @param {Array<{id: any}>} sorted  the rows in their true sorted order (memoised by the caller)
 * @param {object} opts
 * @param {boolean} opts.hold     true while a row has unsaved changes
 * @param {string}  opts.sortKey  changes whenever the user changes the sort
 * @param {any}     opts.editedId the row being edited while `hold` is true
 * @returns {{ rows: Array, revealId: any }}
 */
export default function useHeldRowOrder(sorted, { hold, sortKey, editedId }) {
  const heldIds = useRef(null);
  const heldKey = useRef(sortKey);
  const frozen = !!hold && heldIds.current !== null && heldKey.current === sortKey;

  const rows = useMemo(
    () => (frozen ? holdRowOrder(sorted, heldIds.current) : sorted),
    [frozen, sorted]
  );

  // While not frozen, remember the order on screen — it is what a hold keeps.
  useEffect(() => {
    if (!frozen) {
      heldIds.current = rows.map((r) => r.id);
      heldKey.current = sortKey;
    }
  });

  const [revealId, setRevealId] = useState(null);
  const edited = useRef(null);   // { id, index } while held
  const settling = useRef(null); // { id, index, until } after the hold ends

  useEffect(() => {
    if (hold) {
      settling.current = null;
      if (editedId != null) {
        edited.current = { id: editedId, index: rows.findIndex((r) => r.id === editedId) };
      }
      return;
    }
    if (edited.current) {
      settling.current = { ...edited.current, until: Date.now() + SETTLE_WINDOW_MS };
      edited.current = null;
    }
    const s = settling.current;
    if (!s) return;
    if (Date.now() > s.until) { settling.current = null; return; }
    const index = rows.findIndex((r) => r.id === s.id);
    if (index >= 0 && s.index >= 0 && index !== s.index) {
      settling.current = null;
      setRevealId(s.id);
    }
  }, [hold, editedId, rows]);

  useEffect(() => {
    if (revealId == null) return undefined;
    const t = setTimeout(() => setRevealId(null), HELD_ROW_REVEAL_MS);
    return () => clearTimeout(t);
  }, [revealId]);

  return { rows, revealId };
}
